import produce, { Draft } from 'immer';
import { entries, take } from 'lodash';
import moment from 'moment';
import { createTransform } from 'redux-persist';

import { Action } from '../../actions';
import { PHRASES_SELECTOR_OPEN_EDITOR } from '../../actions/phrases-selector';
import {
    ADD_PHRASES_GENERATOR_GROUP,
    CHANGE_PHRASES_GENERATOR_PHRASES,
    REMOVE_PHRASES_GENERATOR_GROUP
} from '../../actions/phrases-selector/generator';
import {
    PHRASES_SELECTOR_ADD_KEYWORDS,
    PHRASES_SELECTOR_ADD_MINUS_PHRASES,
    PHRASES_SELECTOR_CHANGE_MANUAL_VALUE,
    PHRASES_SELECTOR_CHANGE_MINUS_PHRASE,
    PHRASES_SELECTOR_CHANGE_PRICE_RATE,
    PHRASES_SELECTOR_CLEAR_DATA,
    PHRASES_SELECTOR_CLEAR_KEYWORDS,
    PHRASES_SELECTOR_CLEAR_MINUS_PHRASES,
    PHRASES_SELECTOR_REFINE_KEYWORD,
    PHRASES_SELECTOR_REMOVE_KEYWORD,
    PHRASES_SELECTOR_REMOVE_MINUS_PHRASE,
    PHRASES_SELECTOR_TOGGLE_EXCLUDE_PHRASES
} from '../../actions/phrases-selector/persistentData';
import { GroupId } from '../../typings/groups';
import { PhrasesSelectorPersistentData, PhrasesSelectorsPersistentState } from '../../typings/phrases';
import { PhrasesSelectorsState } from '../../typings/state';

import { phrasesSelectorGeneratorReducer } from './generator';

interface PhrasesSelectorPersistentDataForStorage {
    // этот интерфейс представляет формат данных, хранящихся в localStorage
    // в брауезере пользователя
    // менять его не реомендуется и делать это следует очень аккуратно
    type: 'phrase-persisted-data';
    manualValue: string;
    isExcludePhrases: boolean;
    generator: {
        groups: Array<string>;
    };
    minusPhrases: Array<string>;
    keywords: Array<{
        name: string;
        shows: number;
    }>;
    priceRate?: number;
    lastChange: string;
}

const phrasesSelectorPersistentDataPersistor = (
    data: PhrasesSelectorPersistentData
): PhrasesSelectorPersistentDataForStorage => {
    // это функция маппер из клиентских данных в стейте приложения в localStorage
    // так как интерфейс PhrasesSelectorPersistentDataForStorage не подлжети изменению,
    // этот маппер написан в явном виде, как еще одно напоминание
    return {
        type: 'phrase-persisted-data',
        manualValue: data.manualValue,
        isExcludePhrases: data.isExcludePhrases,
        generator: {
            groups: data.generator.groups
        },
        minusPhrases: data.minusPhrases,
        keywords: data.keywords.map(keyword => ({
            name: keyword.name,
            shows: keyword.shows
        })),
        priceRate: data.priceRate,
        lastChange: data.lastChange
    };
};

const phrasesSelectorPersistentDataRehydrator = (
    data: PhrasesSelectorPersistentDataForStorage
): PhrasesSelectorPersistentData => {
    // это функция маппер из клиентских данных в localStorage браузера в стейт приложения
    // так как интерфейс PhrasesSelectorPersistentDataForStorage не подлжети изменению,
    // этот маппер написан в явном виде, как еще одно напоминание
    return {
        manualValue: data.manualValue,
        isExcludePhrases: data.isExcludePhrases,
        generator: {
            groups: data.generator.groups
        },
        minusPhrases: data.minusPhrases,
        keywords: data.keywords.map(keyword => ({
            name: keyword.name,
            shows: keyword.shows
        })),
        priceRate: data.priceRate,
        lastChange: data.lastChange
    };
};

const MAX_PERSISTED_GROUPS = 5;

export const PhraseSelectorTransform = createTransform(
    // transform state on its way to being serialized and persisted.
    inboundState => {
        return Object.keys(inboundState).reduce<Record<GroupId, PhrasesSelectorPersistentDataForStorage>>(
            (acc, groupId: GroupId) => {
                acc[groupId] = phrasesSelectorPersistentDataPersistor(inboundState[groupId]);
                return acc;
            },
            {}
        );
    },
    // transform state being rehydrated
    outboundState => {
        return take(
            entries(outboundState)
                .filter(([, data]) => data.type === 'phrase-persisted-data')
                .sort(([, data1], [, data2]) => (moment(data1.lastChange).isAfter(moment(data2.lastChange)) ? -1 : 1)),
            MAX_PERSISTED_GROUPS
        ).reduce<PhrasesSelectorsPersistentState>((acc, [groupId, data]) => {
            acc[groupId] = phrasesSelectorPersistentDataRehydrator(data);
            return acc;
        }, {});
    },
    // define which reducers this transform gets called for.
    { whitelist: ['persistentData'] }
);

const phrasesSelectorPersistentInitialData: PhrasesSelectorPersistentData = {
    manualValue: '',
    isExcludePhrases: false,
    generator: {
        groups: ['', '']
    },
    minusPhrases: [],
    keywords: [],
    lastChange: new Date().toISOString()
};

export const getPhrasesSelectorPersistentInitialData = (): PhrasesSelectorPersistentData =>
    phrasesSelectorPersistentInitialData;

export const phrasesSelectorsPersistentReducer = (
    state: PhrasesSelectorsState,
    action: Action
): PhrasesSelectorsState => {
    let groupData: PhrasesSelectorPersistentData | undefined;

    return produce(state, (draftState: Draft<PhrasesSelectorsState>) => {
        phrasesSelectorGeneratorReducer(draftState, action);

        switch (action.type) {
            case PHRASES_SELECTOR_OPEN_EDITOR: {
                if (!draftState.persistentData[action.groupInfo.id]) {
                    draftState.persistentData[action.groupInfo.id] = getPhrasesSelectorPersistentInitialData();
                }

                break;
            }

            case PHRASES_SELECTOR_CLEAR_DATA: {
                delete draftState.persistentData[action.groupId];

                break;
            }

            case PHRASES_SELECTOR_ADD_KEYWORDS: {
                groupData = draftState.persistentData[action.groupId];

                groupData.keywords = [...action.phrases, ...groupData.keywords];
                break;
            }

            case PHRASES_SELECTOR_ADD_MINUS_PHRASES: {
                groupData = draftState.persistentData[action.groupId];

                groupData.minusPhrases = [...action.phrases, ...groupData.minusPhrases];
                break;
            }

            case PHRASES_SELECTOR_REMOVE_KEYWORD: {
                groupData = draftState.persistentData[action.groupId];

                groupData.keywords.splice(action.index, 1);
                break;
            }

            case PHRASES_SELECTOR_REMOVE_MINUS_PHRASE: {
                groupData = draftState.persistentData[action.groupId];

                groupData.minusPhrases.splice(action.index, 1);
                break;
            }

            case PHRASES_SELECTOR_CHANGE_MINUS_PHRASE: {
                const { phrase, groupId } = action;
                groupData = draftState.persistentData[groupId];

                groupData.minusPhrases.splice(action.index, 1, phrase);
                break;
            }

            case PHRASES_SELECTOR_REFINE_KEYWORD: {
                groupData = draftState.persistentData[action.groupId];
                const { name, shows } = action.phrase;

                groupData.keywords[action.index].name = name;
                groupData.keywords[action.index].shows = shows;
                break;
            }

            case PHRASES_SELECTOR_CHANGE_MANUAL_VALUE: {
                groupData = draftState.persistentData[action.groupId];

                groupData.manualValue = action.value;
                break;
            }

            case PHRASES_SELECTOR_TOGGLE_EXCLUDE_PHRASES: {
                groupData = draftState.persistentData[action.groupId];

                groupData.isExcludePhrases = !groupData.isExcludePhrases;
                break;
            }

            case ADD_PHRASES_GENERATOR_GROUP: {
                groupData = draftState.persistentData[action.groupId];

                groupData.generator.groups.push('');
                break;
            }
            case REMOVE_PHRASES_GENERATOR_GROUP: {
                groupData = draftState.persistentData[action.groupId];

                groupData.generator.groups.splice(action.index, 1);
                break;
            }
            case CHANGE_PHRASES_GENERATOR_PHRASES: {
                const { groupId, groupIndex, phrases } = action;
                groupData = draftState.persistentData[groupId];

                groupData.generator.groups[groupIndex] = phrases;
                break;
            }
            case PHRASES_SELECTOR_CLEAR_KEYWORDS: {
                groupData = draftState.persistentData[action.groupId];

                groupData.keywords = [];
                break;
            }
            case PHRASES_SELECTOR_CLEAR_MINUS_PHRASES: {
                groupData = draftState.persistentData[action.groupId];

                groupData.minusPhrases = [];
                break;
            }

            case PHRASES_SELECTOR_CHANGE_PRICE_RATE:
                groupData = draftState.persistentData[action.groupId];

                groupData.priceRate = action.rate;
                break;

            default:
                break;
        }

        if (groupData) {
            groupData.lastChange = new Date().toISOString();
        }
    });
};
